import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function PublicCalculator() {
  const [form, setForm] = useState({ email: "", prize_value: 500, ad_budget: 1000, follower_count: 5000, email_list_size: 2500, gross_margin: 40, avg_order_value: 45, purchase_rate: 30 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/public/success-score", form);
      setResult(data);
    } catch (err) { toast.error("Failed"); }
    setLoading(false);
  };

  const input = "w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
            <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-zinc-700 hover:text-zinc-950">Try the app →</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 bg-white text-xs text-zinc-600">
            <Sparkles className="size-3 text-emerald-600" strokeWidth={1.5} /> Free tool · No signup
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold mt-4">Giveaway Success Calculator</h1>
          <p className="text-zinc-600 mt-3">Forecast entries, revenue, ROAS and profit for any sweepstakes — in 10 seconds.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-md p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">Email</label>
              <input type="email" required data-testid="calc-email-input" placeholder="you@brand.com" className={input} value={form.email} onChange={handle("email")} />
              <div className="text-xs text-zinc-500 mt-1">We'll email your full report. No spam.</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">Prize value ($)</label>
              <input type="number" min={1} data-testid="calc-prize-input" className={input} value={form.prize_value} onChange={handle("prize_value")} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">Ad budget ($)</label>
              <input type="number" min={0} data-testid="calc-budget-input" className={input} value={form.ad_budget} onChange={handle("ad_budget")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">Followers</label>
                <input type="number" min={0} data-testid="calc-followers-input" className={input} value={form.follower_count} onChange={handle("follower_count")} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-2">Email list</label>
                <input type="number" min={0} data-testid="calc-email-list-input" className={input} value={form.email_list_size} onChange={handle("email_list_size")} />
              </div>
            </div>
            <div className="pt-3 border-t border-zinc-100">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Your store economics</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold block mb-1.5">Margin (%)</label>
                  <input type="number" min={0} max={100} data-testid="calc-margin-input" className={input} value={form.gross_margin} onChange={handle("gross_margin")} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold block mb-1.5">Avg order ($)</label>
                  <input type="number" min={0} data-testid="calc-aov-input" className={input} value={form.avg_order_value} onChange={handle("avg_order_value")} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold block mb-1.5">Buy rate (%)</label>
                  <input type="number" min={0} max={100} data-testid="calc-purchase-input" className={input} value={form.purchase_rate} onChange={handle("purchase_rate")} />
                </div>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="calc-submit-button" className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 disabled:opacity-50">
              {loading ? "Calculating…" : "Get my report"}
            </button>
          </form>

          <div className={`relative ${result ? "success-glow" : ""} bg-white border border-zinc-200 rounded-md p-6`} data-testid="calc-result">
            {!result ? (
              <div className="h-full flex items-center justify-center text-center py-16">
                <div>
                  <Sparkles className="size-10 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
                  <div className="text-zinc-500 text-sm">Your forecast appears here.</div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display text-6xl font-bold tabular">{result.score}</div>
                  <div className={`font-display text-3xl font-bold ${result.grade === "A" ? "text-emerald-600" : result.grade === "B" ? "text-zinc-950" : "text-amber-600"}`}>{result.grade}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  {[
                    ["Entries", result.expected_entries.toLocaleString()],
                    ["Revenue", `$${result.expected_revenue.toLocaleString()}`],
                    ["ROAS", result.roas_target ? `${result.roas_target}x` : "—"],
                    ["Profit Est.", `$${result.profit_estimate.toLocaleString()}`],
                    ["Multiplier", `${result.health_score}x`],
                    ["Health", result.health_status],
                  ].map(([k, v]) => (
                    <div key={k} className="border border-zinc-100 rounded-md p-3 bg-zinc-50/50">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{k}</div>
                      <div className="font-display text-lg font-bold tabular mt-1">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-zinc-500" data-testid="calc-economics-note">
                  Based on your economics: <span className="font-medium text-zinc-700">{form.gross_margin}% margin</span> · <span className="font-medium text-zinc-700">${form.avg_order_value} avg order</span> · <span className="font-medium text-zinc-700">{form.purchase_rate}% purchase rate</span>
                </div>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800" data-testid="calc-cta-signup">
                  Launch this giveaway with SweepLaunch <ArrowRight className="size-4" strokeWidth={1.5} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
